import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { FileText, Plus, Trash2, Edit2 } from 'lucide-react';

interface EmailTemplate {
  id: string;
  template_name: string;
  template_type: string;
  subject: string;
  html_content: string;
  is_default: boolean;
}

const defaultTemplates = {
  initial_contact: {
    subject: "Welcome to Premium Smart Toilet Solutions",
    html: `<p>Dear {{leadName}},</p>
<p>Thank you for your interest in Aureum VII Trading's premium smart toilet solutions.</p>
<p>{{customMessage}}</p>
<p>We look forward to helping you transform your bathroom experience.</p>`
  },
  follow_up: {
    subject: "Following Up on Your Smart Toilet Inquiry",
    html: `<p>Dear {{leadName}},</p>
<p>I wanted to follow up on our previous conversation about smart toilet solutions for {{company}}.</p>
<p>{{customMessage}}</p>
<p>Do you have any questions I can help answer?</p>`
  },
  product_specific: {
    subject: "Perfect Smart Toilet Solution for Your Needs",
    html: `<p>Dear {{leadName}},</p>
<p>Based on your requirements, I'd like to introduce you to our premium product line.</p>
<p>{{customMessage}}</p>
<p>Let me know if you'd like to schedule a demonstration.</p>`
  },
  thank_you: {
    subject: "Thank You for Choosing Aureum VII Trading",
    html: `<p>Dear {{leadName}},</p>
<p>Thank you for choosing Aureum VII Trading for your smart toilet needs.</p>
<p>{{customMessage}}</p>
<p>We're excited to work with you!</p>`
  }
};

export const TemplateManager = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    template_name: '',
    template_type: 'initial_contact',
    subject: '',
    html_content: '',
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .eq('agent_id', user.id)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setTemplates(data);
    }
  };

  const handleCreate = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('email_templates')
      .insert([{ ...formData, agent_id: user.id }]);

    if (error) {
      toast.error('Failed to create template');
      return;
    }

    toast.success('Template created successfully');
    setIsCreating(false);
    resetForm();
    fetchTemplates();
  };

  const handleUpdate = async () => {
    if (!editingId) return;

    const { error } = await supabase
      .from('email_templates')
      .update(formData)
      .eq('id', editingId);

    if (error) {
      toast.error('Failed to update template');
      return;
    }

    toast.success('Template updated successfully');
    setEditingId(null);
    resetForm();
    fetchTemplates();
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase
      .from('email_templates')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error('Failed to delete template');
      return;
    }

    toast.success('Template deleted');
    fetchTemplates();
  };

  const loadTemplate = (template: EmailTemplate) => {
    setFormData({
      template_name: template.template_name,
      template_type: template.template_type,
      subject: template.subject,
      html_content: template.html_content,
    });
    setEditingId(template.id);
    setIsCreating(false);
  };

  const useDefaultTemplate = (type: string) => {
    const defaultTemplate = defaultTemplates[type as keyof typeof defaultTemplates];
    setFormData({
      ...formData,
      template_type: type,
      subject: defaultTemplate.subject,
      html_content: defaultTemplate.html,
    });
  };

  const resetForm = () => {
    setFormData({
      template_name: '',
      template_type: 'initial_contact',
      subject: '',
      html_content: '',
    });
    setIsCreating(false);
    setEditingId(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Email Templates</h3>
        <Button onClick={() => setIsCreating(true)} disabled={isCreating || editingId !== null}>
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      {(isCreating || editingId) && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Template' : 'Create New Template'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="template_name">Template Name</Label>
              <Input
                id="template_name"
                value={formData.template_name}
                onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                placeholder="e.g., Welcome Email"
              />
            </div>

            <div>
              <Label htmlFor="template_type">Template Type</Label>
              <Select
                value={formData.template_type}
                onValueChange={(value) => {
                  setFormData({ ...formData, template_type: value });
                  useDefaultTemplate(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="initial_contact">Initial Contact</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="product_specific">Product Specific</SelectItem>
                  <SelectItem value="thank_you">Thank You</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="subject">Email Subject</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="html_content">Email Content (HTML)</Label>
              <Textarea
                id="html_content"
                value={formData.html_content}
                onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                rows={10}
                placeholder="Use {{leadName}}, {{company}}, {{customMessage}} as placeholders"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Available placeholders: {`{{leadName}}`}, {`{{company}}`}, {`{{customMessage}}`}
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={editingId ? handleUpdate : handleCreate}>
                {editingId ? 'Update' : 'Create'} Template
              </Button>
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {templates.map((template) => (
          <Card key={template.id}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4" />
                    <h4 className="font-semibold">{template.template_name}</h4>
                    <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">
                      {template.template_type.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Subject: {template.subject}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadTemplate(template)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(template.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};